import type { CSSObject } from '@emotion/react'

import {
  breakpoint,
  duration,
  easing,
  width,
} from '@twistopayments/design-system/styles'

import {
  legacyFieldPresentation,
  modernFieldPresentation,
} from './field-presentation'
import type { ResolvedTheme } from './themes'
import { textStyle } from './typography.helpers'

type Foundations = Omit<ResolvedTheme, 'presentation'>
type TextPair = { mobile: CSSObject; desktop: CSSObject }

const pair = (mobile: CSSObject, desktop: CSSObject): TextPair => ({
  mobile,
  desktop,
})

const variantText = ({
  mobileLetterSpacing,
  ...metrics
}: NonNullable<
  NonNullable<Foundations['typography']['variants']>['b1']
>): CSSObject => ({
  ...metrics,
  ...(mobileLetterSpacing && {
    [breakpoint.down('sm')]: { letterSpacing: mobileLetterSpacing },
  }),
})

// Temporary compatibility layer for legacy and modern B2C coexistence, not a
// permanent styling abstraction. Empty modern overrides exist only for legacy.
// Once no consumers use b2c-legacy, remove its preset and compatibility-only
// options/style hooks; keep shared helpers and B2C/B2B semantic theme differences.
// Switching the preset alone does not remove this compatibility code.
// Only the values that vary belong here. Structure, selectors and behavior stay
// in components; selection colors are shared by checkboxes, sliders and PINs.
export const modernPresentation = (theme: Foundations): Presentation => {
  const {
    palette: p,
    typography: t,
    spacing: s,
    borderRadius: r,
    shadows,
  } = theme
  const smallText = pair(textStyle(t.app.bodySmMobile), textStyle(t.app.bodySm))

  return {
    field: modernFieldPresentation(theme),
    selection: {
      active: p.accent.highContrast,
      strong: p.accent.maxContrast,
      track: p.accent.lowContrast,
      filled: p.accent.lowContrast,
      onActive: p.tertiary.reversedContrast,
      disabled: p.disabled.lowContrast,
      disabledBorder: 'transparent',
    },
    action: {
      resend: 'outlined',
      frame: {},
      sizes: { small: {}, medium: {}, large: {} },
      secondary: {},
      ghost: {},
      reversedOutline: {},
      disabled: {},
      text: {
        small: smallText,
        medium: smallText,
        large: pair(textStyle(t.app.bodyMobile), textStyle(t.app.body)),
      },
    },
    choice: {
      frame: {},
      iconGap: s[12],
      align: 'center',
      labelColor: 'textBody',
      label: {},
      smallLabel: {},
      content: {},
      interaction: {},
      helper: {},
      helperColor: p.text.description,
    },
    range: {
      thumbArtwork: theme.brand === 'b2b' ? 'b2b' : theme.themeName,
      frame: {},
      margin: '0',
      unfilled: p.accent.lowContrast,
      shadow: shadows.none,
      interactionShadow: shadows.elevationHover,
      footerGap: s[12],
      label: pair(textStyle(t.app.captionMobile), textStyle(t.app.caption)),
    },
    pin: {
      frame: {},
      character: {},
      current: {},
      caret: 'after',
    },
    surface: {
      fullHeightMobile: { margin: '0 auto', maxWidth: width.container.tiny },
      modalCorners: `${r.xs} ${r.xl}`,
      modalDesktopCorners: undefined,
      modalDesktopPadding: s[48],
      bannerRadius: r.sm,
      banner: {},
      secondary: p.secondary.lowContrast,
      secondaryHover: p.secondary.lowerMidContrast,
    },
    banner: {
      title: {},
      uppercase: {},
      subtitle: { small: {}, medium: {} },
      link: { small: {}, medium: {} },
      iconOffset: { small: {}, medium: {} },
      iconFrame: {},
      iconMedium: {},
      iconPrimaryBackground: p.primary.lowContrast,
      iconSecondaryBackground: p.secondary.lowContrast,
      iconSecondaryColor: p.secondary.highContrast,
      hover: { primary: {}, secondary: {}, warning: {}, error: {} },
    },
    badge: {
      small: pair(
        { fontSize: '9px', lineHeight: s[16] },
        { fontSize: '11px', lineHeight: s[20] }
      ),
      medium: pair(
        { fontSize: '11px', lineHeight: s[20] },
        { fontSize: '13px', lineHeight: s[24] }
      ),
      smallDesktopPadding: '6px',
    },
    navigation: {
      curveRadius: r.md,
      mobileColor: p.tertiary.highContrast,
      activeColor: p.accent.highContrast,
      color: p.text.body,
      hoverColor: p.accent.maxContrast,
      hoverBackground: p.accent.lowerMidContrast,
      activeBackground: p.accent.lowContrast,
      activeInteraction: {},
    },
  }
}

export type Presentation = {
  field: ReturnType<typeof modernFieldPresentation>
  selection: {
    active: string
    strong: string
    track: string
    filled: string
    onActive: string
    disabled: string
    disabledBorder: string
  }
  action: {
    resend: 'inline' | 'outlined'
    frame: CSSObject
    sizes: Record<'small' | 'medium' | 'large', CSSObject>
    secondary: CSSObject
    ghost: CSSObject
    reversedOutline: CSSObject
    disabled: CSSObject
    text: Record<'small' | 'medium' | 'large', TextPair>
  }
  choice: {
    frame: CSSObject
    iconGap: string
    align: string
    labelColor: 'textBody' | 'textSecondary'
    label: CSSObject
    smallLabel: CSSObject
    content: CSSObject
    interaction: CSSObject
    helper: CSSObject
    helperColor: string
    disabledHelperColor?: string
  }
  range: {
    thumbArtwork: string
    frame: CSSObject
    margin: string
    unfilled: string
    shadow: string
    interactionShadow: string
    footerGap: string
    label: TextPair
  }
  pin: {
    frame: CSSObject
    character: CSSObject
    current: CSSObject
    caret: 'before' | 'after'
  }
  surface: {
    fullHeightMobile: CSSObject
    modalCorners: string
    modalDesktopCorners?: string
    modalDesktopPadding: string
    bannerRadius: string
    banner: CSSObject
    secondary: string
    secondaryHover: string
  }
  banner: {
    title: CSSObject
    uppercase: CSSObject
    subtitle: Record<'small' | 'medium', CSSObject>
    link: Record<'small' | 'medium', CSSObject>
    iconOffset: Record<'small' | 'medium', CSSObject>
    iconFrame: CSSObject
    iconMedium: CSSObject
    iconPrimaryBackground: string
    iconSecondaryBackground: string
    iconSecondaryColor: string
    hover: Record<'primary' | 'secondary' | 'warning' | 'error', CSSObject>
  }
  badge: { small: TextPair; medium: TextPair; smallDesktopPadding: string }
  navigation: {
    curveRadius: string
    mobileColor: string
    activeColor: string
    color: string
    hoverColor: string
    hoverBackground: string
    activeBackground: string
    activeInteraction: CSSObject
  }
}
export type PresentationOverrides = {
  [K in keyof Presentation]?: Partial<Presentation[K]>
}

export const legacyPresentation = (theme: Foundations): Presentation => {
  const modern = modernPresentation(theme)
  const { palette: p, spacing: s, typography: t, borderRadius: r } = theme
  const small = t.variants?.b3 ?? {
    fontSize: '0.875rem',
    lineHeight: '1.375rem',
    letterSpacing: '-0.3px',
  }
  const medium = t.variants?.b2 ?? {
    fontSize: '1.0625rem',
    lineHeight: '1.5rem',
    letterSpacing: '-0.4px',
  }
  const large = t.variants?.b1 ?? {
    fontSize: '1.125rem',
    lineHeight: '1.625rem',
    letterSpacing: '-0.4px',
  }
  const rangeLabel = t.variants?.b4 ?? {
    fontSize: '0.8125rem',
    lineHeight: '1.125rem',
    letterSpacing: '-0.3px',
  }
  const thumbShadow = '0 4px 4px 0 rgba(36, 34, 42, 0.08)'
  const bannerText = (metrics: typeof small): CSSObject => {
    const style = {
      fontSize: metrics.fontSize,
      lineHeight: metrics.lineHeight,
      letterSpacing: '-0.01em',
    }

    return { ...style, [breakpoint.up('sm')]: style }
  }
  const iconHover: CSSObject = {
    '& .banner-icon': { transform: 'scale3d(1.2, 1.2, 1)' },
  }

  return {
    ...modern,
    field: legacyFieldPresentation(theme),
    selection: {
      active: p.secondary.mainContrast,
      strong: p.secondary.mainContrast,
      track: p.secondary.lowContrast,
      filled: p.secondary.mediumContrast,
      onActive: p.secondary.reversedContrast,
      disabled: p.disabled.main,
      disabledBorder: p.disabled.accent,
    },
    action: {
      resend: 'inline',
      frame: { gap: 'normal', whiteSpace: 'normal', boxShadow: 'none' },
      sizes: {
        small: { padding: `0 ${s[16]}`, minHeight: s[32] },
        medium: { padding: `0 ${s[24]}`, minHeight: s[48] },
        large: {
          padding: `0 ${s[24]}`,
          minHeight: s[48],
          [breakpoint.up('md')]: { padding: `0 ${s[32]}`, minHeight: s[64] },
        },
      },
      secondary: {
        border: 'none',
        color: p.primary.mainContrast,
        background: p.primary.lowContrast,
        '&:hover': { background: p.primary.mediumContrast },
        '&:focus-visible': { outlineColor: p.primary.mediumContrast },
      },
      ghost: {
        color: p.primary.mainContrast,
        '&:hover': {
          background: p.primary.lowContrast,
          color: p.primary.mainContrast,
        },
      },
      reversedOutline: {
        border: 'none',
        outline: `1px solid ${p.secondary.reversedContrast}`,
        '&:hover': { outlineColor: p.primary.maxContrast },
      },
      disabled: { background: p.disabled.main },
      text: {
        small: pair(variantText(small), variantText(small)),
        medium: pair(variantText(medium), variantText(medium)),
        large: pair(variantText(medium), variantText(large)),
      },
    },
    choice: {
      frame: { padding: s[8], minHeight: s[48], color: p.text.title },
      iconGap: s[24],
      align: 'flex-start',
      labelColor: 'textSecondary',
      content: { gap: 0, [breakpoint.up('sm')]: { gap: 0 } },
      interaction: {
        '&:hover + div': { boxShadow: `0 0 0 4px ${p.ghost.lowContrast}` },
        '&:active + div, &:active:hover + div': {
          boxShadow: `0 0 0 4px ${p.ghost.mediumContrast}`,
        },
      },
      helper: {
        marginLeft: '-16px',
        marginTop: s[4],
        p: variantText(small),
        [breakpoint.up('sm')]: { p: variantText(small) },
      },
      helperColor: p.tertiary.mediumContrastAlternative,
      disabledHelperColor: p.ghost.mediumContrast,
      label: {
        lineHeight: 1.6,
        minHeight: '22px',
        fontSize: '13px',
        letterSpacing: '-0.13px',
        marginTop: '2px',
        [breakpoint.up('sm')]: {
          fontSize: '15px',
          letterSpacing: '-0.15px',
          marginTop: 0,
          minHeight: s[24],
        },
      },
      smallLabel: {
        lineHeight: 1.6,
        minHeight: s[20],
        fontSize: '11px',
        letterSpacing: '-0.11px',
        marginTop: '4px',
        [breakpoint.up('sm')]: {
          fontSize: '11px',
          lineHeight: 1.6,
          letterSpacing: '-0.11px',
        },
      },
    },
    range: {
      thumbArtwork: modern.range.thumbArtwork,
      frame: {
        padding: '0 14px',
        margin: '8px 0',
        borderRadius: s[4],
        background: p.secondary.lowContrast,
      },
      margin: '8px 0',
      unfilled: p.secondary.mediumContrast,
      shadow: thumbShadow,
      interactionShadow: thumbShadow,
      footerGap: s[4],
      label: pair(rangeLabel, rangeLabel),
    },
    pin: {
      frame: {
        maxWidth: '20px',
        margin: '0 10px',
        padding: '6px',
        fontSize: theme.fontSize.xxl,
        lineHeight: theme.fontSize.xxl,
        letterSpacing: 'normal',
        fontWeight: t.fontWeight.bold,
        transition: `transform ${duration.short}ms`,
        transform: 'scale(0.8)',
        position: 'relative',
        boxShadow: 'none',
        background: p.tertiary.mediumContrast,
        [breakpoint.up('sm')]: {
          maxWidth: '24px',
          margin: '0 12px',
          padding: s[8],
          fontSize: theme.fontSize.xxl,
          lineHeight: theme.fontSize.xxl,
          letterSpacing: 'normal',
        },
        '&:after': { background: p.tertiary.upperMediumContrast },
        '&:before': {
          display: 'block',
          height: '8px',
          width: '8px',
          content: "''",
          borderRadius: r.circle,
          opacity: 0,
          position: 'absolute',
          background: p.tertiary.main,
        },
        'label:focus-within &, label:hover &': { transform: 'scale(1)' },
      },
      character: {
        width: 'auto',
        padding: '6px',
        transform: 'scale(1)',
        transition: `color ${duration.short}ms`,
        [breakpoint.up('sm')]: { padding: s[8] },
      },
      current: {
        background: p.tertiary.mediumContrast,
        '&:after': { background: p.tertiary.upperMediumContrast },
        'label:focus-within &, label:hover &': {
          boxShadow: `0 0 0 2px ${p.tertiary.upperMediumContrast}`,
        },
      },
      caret: 'before',
    },
    surface: {
      fullHeightMobile: {},
      modalCorners: `${r.sm} ${r.lg}`,
      modalDesktopCorners: `${r.md} ${r.xl}`,
      modalDesktopPadding: '45px',
      bannerRadius: r.md,
      banner: {
        columnGap: 'normal',
        rowGap: 'normal',
        alignItems: 'normal',
        [breakpoint.up('sm')]: { columnGap: 'normal', rowGap: 'normal' },
        '& .banner-icon-wrapper': { transformStyle: 'preserve-3d' },
        '& .banner-icon': {
          transformOrigin: 'center center',
          transition: `transform ${easing.easeInOut} ${duration.standard}ms`,
        },
      },
      secondary: p.tertiary.lowContrast,
      secondaryHover: p.tertiary.mediumContrast,
    },
    banner: {
      title: { ...bannerText(small), marginBottom: s[4] },
      uppercase: {
        letterSpacing: 'initial',
        marginBottom: 0,
        [breakpoint.up('sm')]: { letterSpacing: 'initial', marginBottom: s[4] },
      },
      subtitle: { small: bannerText(small), medium: bannerText(medium) },
      link: {
        small: {
          ...variantText(small),
          [breakpoint.up('sm')]: variantText(small),
        },
        medium: {
          ...variantText(medium),
          [breakpoint.up('sm')]: variantText(medium),
        },
      },
      iconOffset: {
        small: {
          marginRight: s[8],
          [breakpoint.up('sm')]: { marginRight: s[12] },
        },
        medium: {
          marginRight: s[8],
          [breakpoint.up('sm')]: { marginRight: s[16] },
        },
      },
      iconFrame: {
        width: '36px',
        height: '36px',
        [breakpoint.up('sm')]: { width: s[48], height: s[48] },
      },
      iconMedium: {
        width: s[24],
        height: s[24],
        [breakpoint.up('sm')]: { width: s[32], height: s[32] },
      },
      iconPrimaryBackground: p.primary.mediumContrast,
      iconSecondaryBackground: p.tertiary.lowContrast,
      iconSecondaryColor: p.tertiary.main,
      hover: {
        primary: {
          '&:hover': {
            ...iconHover,
            backgroundColor: p.primary.mediumContrast,
            '& .banner-title-highlighted': { color: p.primary.maxContrast },
            '& .banner-icon-wrapper': {
              backgroundColor: p.primary.lowContrast,
            },
          },
        },
        secondary: {
          '&:hover': {
            ...iconHover,
            '& .banner-icon-wrapper': {
              backgroundColor: p.tertiary.lowContrast,
            },
          },
        },
        warning: { '&:hover': iconHover },
        error: { '&:hover': iconHover },
      },
    },
    badge: {
      small: pair(
        { fontSize: '12px', lineHeight: s[18], letterSpacing: '0.12px' },
        { fontSize: '13px', lineHeight: s[20], letterSpacing: '0.13px' }
      ),
      medium: pair(
        { fontSize: '13px', lineHeight: s[18], letterSpacing: '0.13px' },
        { fontSize: '14px', lineHeight: s[24], letterSpacing: '0.14px' }
      ),
      smallDesktopPadding: s[4],
    },
    navigation: {
      curveRadius: r.xl,
      mobileColor: p.ghost.mediumContrast,
      activeColor: p.primary.mainContrast,
      color: p.text.description,
      hoverColor: p.text.description,
      hoverBackground: p.tertiary.lowContrast,
      activeBackground: p.primary.lowContrast,
      activeInteraction: {
        ':hover, :focus': { color: p.primary.mainContrast },
      },
    },
  }
}
